"use client";

import { Crown, MapPin, Calendar } from "lucide-react";
import { IExecutive } from "@/models/Executive";
import Image from "next/image";

interface ExecutiveCardProps {
  executive: IExecutive;
  isPresident?: boolean;
}

const ExecutiveCard = ({
  executive,
  isPresident = false,
}: ExecutiveCardProps) => {
  return (
    <div
      className={`
        group relative bg-accent-200 z-0 rounded-xl shadow-lg overflow-hidden 
        transition-all duration-300 hover:shadow-2xl hover:-translate-y-1
        flex flex-col h-full
        ${isPresident ? "ring-2 ring-yellow-400 ring-offset-2" : ""}
      `}
    >
      {/* President Badge */}
      {isPresident && (
        <div className="absolute top-3 left-3 z-10">
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <Crown className="w-4 h-4" />
            <span className="text-xs font-bold tracking-wider">PRESIDENT</span>
          </div>
        </div>
      )}

      {/* Large Image Section - Like E-commerce Product */}
      <div className="relative aspect-square w-full overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        {executive.avatar ? (
          <img
            src={executive.avatar}
            alt={`${executive.name}'s profile picture`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/default-avatar.png";
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <div className="text-xl font-bold text-gray-300">
              {executive.name.charAt(0)}
            </div>
          </div>
        )}

        {/* Image Overlay Effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Information Section - Like Product Details */}
      <div className="flex flex-col flex-grow p-5">
        {/* Name & Position */}
        <div className="mb-3">
          <h3 className=" font-bold text-sm text-gray-900 line-clamp-1 mb-1">
            {executive.name}
          </h3>
          <div className="flex items-center justify-between">
            <span
              className={`
                inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold
                ${
                  isPresident
                    ? "bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border border-yellow-200"
                    : "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 border border-blue-200"
                }
              `}
            >
              {executive.position}
            </span>

            
          </div>
        </div>

        {/* Details Grid */}
        <div className="space-y-3 border-t border-gray-100 pt-3 flex-grow">
          {/* Institution */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500">Institution</p>
              <p className="text-sm text-gray-900 truncate">
                {executive.institution}
              </p>
            </div>
          </div>

          {/* Session */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500">Session</p>
              <p className="text-sm text-gray-900">{executive.session}</p>
            </div>
          </div>

          {/* State (Conditional)
          {executive.state && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500">State</p>
                <p className="text-sm text-gray-900">{executive.state}</p>
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default ExecutiveCard;
